import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';
import { FaBell, FaTimes, FaCheck, FaUserFriends, FaEye, FaEdit, FaCheckCircle, FaTimesCircle, FaExclamationTriangle } from 'react-icons/fa';

export default function Notificacoes({ user, onPropriedadeClick }) {
  const [notificacoes, setNotificacoes] = useState([]);
  const [mostrar, setMostrar] = useState(false);
  const [naoLidas, setNaoLidas] = useState(0);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!user?.id) return;
    buscarNotificacoes();
    
    // Atualizar a cada 30 segundos
    const interval = setInterval(buscarNotificacoes, 30000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (!mostrar) return;

    const handlePointerDown = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setMostrar(false);
      }
    };

    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setMostrar(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [mostrar]);

  const buscarNotificacoes = async () => {
    if (!user?.id) return;

    const { data } = await supabase
      .from('notificacoes')
      .select('*')
      .eq('user_id', user.id)
      .order('criado_em', { ascending: false })
      .limit(20);

    setNotificacoes(data || []);
    setNaoLidas((data || []).filter(n => !n.lida).length);
  };

  const marcarComoLida = async (id) => {
    await supabase
      .from('notificacoes')
      .update({ lida: true })
      .eq('id', id);
    
    buscarNotificacoes();
  };

  const marcarTodasComoLidas = async () => {
    await supabase
      .from('notificacoes')
      .update({ lida: true })
      .eq('user_id', user.id)
      .eq('lida', false);
    
    buscarNotificacoes();
  };

  const apagarNotificacao = async (id, e) => {
    e.stopPropagation(); // Evita que clique no X também clique na notificação
    await supabase
      .from('notificacoes')
      .delete()
      .eq('id', id);
    
    buscarNotificacoes();
  };

  const apagarTodas = async () => {
    if (!window.confirm('Deseja realmente apagar todas as notificações?')) return;
    
    await supabase
      .from('notificacoes')
      .delete()
      .eq('user_id', user.id);
    
    buscarNotificacoes();
  };

  const aceitarCompartilhamento = async (notif, e) => {
    e.stopPropagation();
    
    try {
      // Atualizar status do compartilhamento para 'aceito'
      const { error } = await supabase
        .from('propriedades_compartilhadas')
        .update({ status: 'aceito' })
        .eq('propriedade_id', notif.dados?.propriedade_id)
        .eq('user_id', user.id);
      
      if (error) {
        console.error('Erro ao aceitar:', error);
        alert('Erro ao aceitar compartilhamento');
        return;
      }
      
      // Marcar notificação como lida
      await marcarComoLida(notif.id);
      
      // Recarregar notificações
      buscarNotificacoes();
      
      // Feedback visual
      alert('Compartilhamento aceito! A propriedade agora aparece em "Minhas Propriedades".');
    } catch (err) {
      console.error('Erro:', err);
      alert('Erro ao aceitar compartilhamento');
    }
  };

  const rejeitarCompartilhamento = async (notif, e) => {
    e.stopPropagation();
    
    try {
      // Atualizar status do compartilhamento para 'rejeitado'
      const { error } = await supabase
        .from('propriedades_compartilhadas')
        .update({ status: 'rejeitado', ativo: false })
        .eq('propriedade_id', notif.dados?.propriedade_id)
        .eq('user_id', user.id);
      
      if (error) {
        console.error('Erro ao rejeitar:', error);
        alert('Erro ao rejeitar compartilhamento');
        return;
      }
      
      // Apagar a notificação
      await apagarNotificacao(notif.id, e);
    } catch (err) {
      console.error('Erro:', err);
      alert('Erro ao rejeitar compartilhamento');
    }
  };

  const getTipoIcon = (tipo) => {
    if (tipo === 'compartilhamento') return <FaUserFriends color="#1976d2" />;
    if (tipo === 'vencimento_plano') return <FaExclamationTriangle color="#f57c00" />;
    if (tipo === 'novo_cadastro') return <FaCheckCircle color="#2e7d32" />;
    return <FaBell color="#666" />;
  };

  const getRoleIcon = (role) => {
    if (role === 'editor') return <FaEdit color="#2196f3" size={14} />;
    if (role === 'viewer') return <FaEye color="#9e9e9e" size={14} />;
    return null;
  };

  const getRoleLabel = (role) => {
    if (role === 'editor') return 'Editor';
    if (role === 'viewer') return 'Visualizador';
    return role;
  };

  const formatarDataLocal = (dateValue) => {
    if (!dateValue) return '';

    // Alguns registros podem vir sem sufixo de timezone; neste caso,
    // tratamos como UTC para converter corretamente ao horário local.
    const raw = String(dateValue);
    const hasTimezone = /([zZ]|[+-]\d{2}:\d{2})$/.test(raw);
    const normalized = hasTimezone ? raw : `${raw}Z`;
    const dt = new Date(normalized);

    if (Number.isNaN(dt.getTime())) return raw;

    return dt.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', zIndex: 220 }}>
      {/* Botão de notificações */}
      <button
        onClick={() => setMostrar(!mostrar)}
        style={{
          position: 'relative',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: 24,
          color: '#1976d2',
          padding: 8
        }}
      >
        <FaBell />
        {naoLidas > 0 && (
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            background: '#d32f2f',
            color: '#fff',
            borderRadius: '50%',
            width: 18,
            height: 18,
            fontSize: 11,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {naoLidas > 9 ? '9+' : naoLidas}
          </div>
        )}
      </button>

      {/* Painel de notificações */}
      {mostrar && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 10px)',
            right: 0,
            width: 'min(430px, calc(100vw - 20px))',
            maxHeight: '72vh',
            overflow: 'auto',
            background: '#fff',
            borderRadius: 14,
            boxShadow: '0 14px 40px rgba(0,0,0,0.24)',
            border: '1px solid #e6edf6',
            zIndex: 9999
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 14,
              overflow: 'hidden'
            }}
          >
            {/* Header */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              background: '#fff',
              zIndex: 1
            }}>
              <h3 style={{ margin: 0, color: '#1976d2', fontSize: 18 }}>
                <FaBell style={{ marginRight: 8 }} />
                Notificações {naoLidas > 0 && `(${naoLidas})`}
              </h3>
              <div style={{ display: 'flex', gap: 8 }}>
                {naoLidas > 0 && (
                  <button
                    onClick={marcarTodasComoLidas}
                    style={{
                      background: '#e3f2fd',
                      color: '#1976d2',
                      border: 'none',
                      borderRadius: 6,
                      padding: '6px 12px',
                      fontSize: 12,
                      cursor: 'pointer',
                      fontWeight: 600
                    }}
                  >
                    Marcar todas como lidas
                  </button>
                )}
                {notificacoes.length > 0 && (
                  <button
                    onClick={apagarTodas}
                    style={{
                      background: '#ffebee',
                      color: '#d32f2f',
                      border: 'none',
                      borderRadius: 6,
                      padding: '6px 12px',
                      fontSize: 12,
                      cursor: 'pointer',
                      fontWeight: 600
                    }}
                  >
                    Apagar todas
                  </button>
                )}
                <button
                  onClick={() => setMostrar(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 20,
                    cursor: 'pointer',
                    color: '#666'
                  }}
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            {/* Lista de notificações */}
            <div>
              {notificacoes.length === 0 ? (
                <div style={{
                  padding: 40,
                  textAlign: 'center',
                  color: '#999',
                  fontSize: 14
                }}>
                  <FaBell size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
                  <div>Nenhuma notificação</div>
                </div>
              ) : (
                notificacoes.map((notif) => (
                  <div
                    key={notif.id}
                    style={{
                      padding: '12px 20px',
                      borderBottom: '1px solid #f5f5f5',
                      background: notif.lida ? '#fff' : '#f0f7ff',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onClick={() => {
                      marcarComoLida(notif.id);
                      if (notif.tipo === 'compartilhamento' && notif.dados?.propriedade_id && onPropriedadeClick) {
                        setMostrar(false);
                        onPropriedadeClick(notif.dados.propriedade_id);
                      }
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#e3f2fd'}
                    onMouseLeave={(e) => e.currentTarget.style.background = notif.lida ? '#fff' : '#f0f7ff'}
                  >
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <div style={{ fontSize: 20, marginTop: 2 }}>
                        {getTipoIcon(notif.tipo)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, color: '#333', marginBottom: 4, fontSize: 14 }}>
                          {notif.titulo}
                        </div>
                        <div style={{ color: '#666', fontSize: 13, marginBottom: 6, lineHeight: 1.5 }}>
                          {notif.mensagem}
                        </div>
                        {notif.dados?.role && (
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 12,
                            color: notif.dados.role === 'editor' ? '#2196f3' : '#666',
                            fontWeight: 600,
                            marginBottom: 6
                          }}>
                            {getRoleIcon(notif.dados.role)} {getRoleLabel(notif.dados.role)}
                          </div>
                        )}
                        
                        {/* Botões de Aceitar/Rejeitar para compartilhamentos */}
                        {notif.tipo === 'compartilhamento' && !notif.lida && (
                          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <button
                              onClick={(e) => aceitarCompartilhamento(notif, e)}
                              style={{
                                background: '#4caf50',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 6,
                                padding: '6px 12px',
                                fontSize: 12,
                                cursor: 'pointer',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4
                              }}
                            >
                              <FaCheckCircle /> Aceitar
                            </button>
                            <button
                              onClick={(e) => rejeitarCompartilhamento(notif, e)}
                              style={{
                                background: '#f44336',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 6,
                                padding: '6px 12px',
                                fontSize: 12,
                                cursor: 'pointer',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4
                              }}
                            >
                              <FaTimesCircle /> Rejeitar
                            </button>
                          </div>
                        )}
                        
                        <div style={{ fontSize: 11, color: '#999' }}>
                          {formatarDataLocal(notif.criado_em)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {!notif.lida && (
                          <div style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: '#1976d2'
                          }} />
                        )}
                        <button
                          onClick={(e) => apagarNotificacao(notif.id, e)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#999',
                            fontSize: 16,
                            padding: 4,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 4,
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#ffebee';
                            e.currentTarget.style.color = '#d32f2f';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'none';
                            e.currentTarget.style.color = '#999';
                          }}
                          title="Apagar notificação"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
